import type { WithT } from 'i18next';
import debounce from 'lodash.debounce';
import type { IObservableArray } from 'mobx';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Account,
  Campaign,
  Domain,
  EmailVerification,
  EmailVerifications as EmailVerificationsCollection,
  Goal,
  MailrailAutoSendEmailCampaign,
  MailrailBaseEmailCampaign,
  MailrailDripEmailCampaign,
  Segment,
  Template,
  TFlagsRecord,
} from '@feathr/blackbox';
import {
  CampaignState,
  EPinpointRequestStatus,
  MailrailSingleSendEmailCampaign,
} from '@feathr/blackbox';
import type { IWizardStepsCompleted } from '@feathr/components';
import { Step, Steps, useWizardState, Wizard } from '@feathr/components';
import { useAccount, useFlags, useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT, flattenError, flattenErrors } from '@feathr/hooks';
import type { ListResponse, Model } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import CampaignWizardActionBar from '../CampaignWizardActionBar';
import { EmailAudienceConfirmModal } from '../EmailAudienceConfirmModal';
import PendingImportsConfirmModal from '../PendingImportsConfirmModal';
import SaveCampaignButton from '../SaveCampaignButton';
import StepGoals, { getGoalSegments, validateStepGoals } from '../StepGoals';
import StepValue, { validateStepValue } from '../StepValue';
import MailrailCampaignStepAutomation, {
  validateStepAutomation,
} from './MailrailCampaignStepAutomation';
import MailrailCampaignStepConfigureDetails, {
  validateStepConfigureDetails,
} from './MailrailCampaignStepConfigureDetails';
import MailrailCampaignStepDripBuilder, {
  validateStepDripBuilder,
} from './MailrailCampaignStepDripBuilder';
import MailrailCampaignStepScheduleSend, {
  validateStepScheduleSend,
} from './MailrailCampaignStepScheduleSend';
import MailrailCampaignStepTest from './MailrailCampaignStepTest';
import MailrailChangeConfirmModal from './MailrailChangeConfirmModal';
import { useMailrailSaveGuards } from './useMailrailSaveGuards';

import * as styles from './MailrailCampaignEdit.css';

interface IWizardStepConfig {
  key: string;
  title: string;
  errors: string[];
  containerClass: string;
  render: () => JSX.Element;
}

interface IProps {
  campaign: MailrailBaseEmailCampaign;
}

export interface IValidatePinpointCampaignProps extends WithT {
  account: Account;
  campaign: MailrailBaseEmailCampaign;
  emailVerification?: EmailVerification;
  emailVerifications?: ListResponse<EmailVerification>;
  goals: IObservableArray<Goal>;
  goalSegments?: Segment[];
  domains?: IObservableArray<Domain>;
  segments?: Segment[];
  template?: Template;
  templates?: readonly Template[];
  flags?: Partial<TFlagsRecord>;
}

const getEmailVerifications = debounce(
  (campaign: MailrailBaseEmailCampaign, EmailVerifications: EmailVerificationsCollection) => {
    return campaign.getEmailVerifications(EmailVerifications);
  },
  DEFAULT_DEBOUNCE_WAIT,
  { leading: true },
);

export function getIsVerifiedDomainSender(
  domains: IObservableArray<Domain>,
  emailVerification?: EmailVerification,
  bypassMX?: boolean,
): boolean {
  if (!emailVerification || emailVerification.isEphemeral) {
    /*
     * Return early to ensure we only call .split
     * on a valid email address
     */
    return false;
  }
  const senderDomain = emailVerification.get('email').split('@')[1];
  const verifiedDomains = domains
    .filter((domain) => domain.emailSendingStatus(bypassMX) === EPinpointRequestStatus.Success)
    .map((domain) => domain.get('email_domain'));

  /*
   * Returns true if the sender domain matches or is a subdomain of
   * one of the account's authorized domains
   */
  return verifiedDomains.some(
    (domain) => senderDomain === domain || senderDomain.endsWith('.' + domain),
  );
}

function validate({
  account,
  campaign,
  emailVerification,
  emailVerifications,
  goals,
  goalSegments,
  domains,
  segments,
  template,
  templates,
  t,
  flags,
}: IValidatePinpointCampaignProps): ICampaignValidationErrors {
  const automation = campaign.isMailrailAutoSend
    ? validateStepAutomation(campaign as MailrailAutoSendEmailCampaign)
    : {};
  const goalsOrValue = campaign.isMonetization
    ? validateStepValue(campaign)
    : validateStepGoals({ goals, goalSegments });
  const send = validateStepScheduleSend(campaign);
  const isDripOrAuto = campaign.isMailrailAutoSend || campaign.isMailrailDrip;

  const validationErrors: ICampaignValidationErrors = {
    monetization_value: (goalsOrValue.monetization_value as string[]) ?? [],
    goals: (goalsOrValue.goals as string[]) ?? [],
    segments_excluded: send.segments_excluded ?? [],
    segments_included: send.segments_included ?? [],
    date_start: send.date_start ?? [],
    'consent.has_consent': send['consent.has_consent'] ?? [],
  };

  // The campaign-document fields (name / sender / contact info / template / sender-verification
  // status) come from the shared Configure-Details validator. A drip validates these per-card in the
  // Builder (folded into `drip_steps`), so skip Configure-Details entirely for a drip.
  if (!campaign.isMailrailDrip) {
    const details = validateStepConfigureDetails(campaign, emailVerification, template);
    validationErrors['template'] = details.template ?? [];
    validationErrors['name'] = details.name ?? [];
    validationErrors['from_address'] = details.from_address ?? [];
    validationErrors['from_name'] = details.from_name ?? [];
    validationErrors['contact_info.company_name'] = details['contact_info.company_name'] ?? [];
    validationErrors['contact_info.premise1'] = details['contact_info.premise1'] ?? [];
    validationErrors['contact_info.locality'] = details['contact_info.locality'] ?? [];
    validationErrors['contact_info.administrative_area_name'] =
      details['contact_info.administrative_area_name'] ?? [];
    validationErrors['contact_info.postal_code'] = details['contact_info.postal_code'] ?? [];
    validationErrors['contact_info.country_code'] = details['contact_info.country_code'] ?? [];
    validationErrors['status'] = details.status ?? [];
  }
  if (isDripOrAuto) {
    validationErrors['bulk_send_enabled'] = send.bulk_send_enabled ?? [];
    validationErrors['bulk_send_timing'] = send.bulk_send_timing ?? [];
    validationErrors['date_end'] = send.date_end ?? [];
  }
  if (campaign.isMailrailAutoSend) {
    // Per-step delay/predicates are the autosend automation step; for a drip these live in the
    // Builder and surface via drip_steps below.
    validationErrors['delay_seconds'] = automation.delay_seconds ?? [];
    validationErrors['predicates'] = automation.predicates ?? [];
  }
  if (campaign.isMailrailDrip) {
    validationErrors['date_enrollment_end'] = send.date_enrollment_end ?? [];
    // The Builder's per-card + campaign-name aggregate. Computed here (like every other step's
    // keys) so Publish is gated on it; the action bar flattens every key of this object.
    validationErrors['drip_steps'] = validateStepDripBuilder(
      campaign as MailrailDripEmailCampaign,
      emailVerifications,
      templates,
    ).dripStepsErrors;
  }
  if (campaign.isMailrailPartnerMessage) {
    validationErrors['participation.mode'] = send['participation.mode'] ?? [];
    validationErrors['participation.partner_ids'] = send['participation.partner_ids'] ?? [];
  }
  if (campaign.isMonetization) {
    validationErrors['monetization_value'] = flattenError(goalsOrValue.monetization_value);
  } else {
    validationErrors['goals'] = flattenError(goalsOrValue.goals);
  }

  if (account.get('email_health') === 'suspended') {
    validationErrors.account = [
      t('Your Feathr account is suspended and cannot publish any new email campaigns.'),
    ];
  }

  if (
    emailVerification &&
    segments &&
    !getIsVerifiedDomainSender(domains!, emailVerification, flags?.bypassMX) &&
    (!isDripOrAuto || campaign.get('bulk_send_enabled'))
  ) {
    const targetedPeople = segments.reduce((total, segment) => {
      return total + (segment.get('stats_cache')?.num_emails_primary || 0);
    }, 0);

    if (targetedPeople > 1000) {
      validationErrors.contacts_limit = [
        t('You cannot target more than 1,000 contacts unless you authorize your sender domain.'),
      ];
    }
  }

  return validationErrors;
}

function MailrailCampaignEdit({ campaign }: Readonly<IProps>): JSX.Element {
  const account = useAccount();
  const flags = useFlags();
  const { Domains, EmailVerifications, Goals, Segments, Templates } = useStore();
  const { t } = useTranslation();

  const domains = Domains.list();

  const template = campaign.getTemplate(Templates);
  const templates = campaign.getTemplates(Templates);
  const includedSegmentIds = campaign.get('segments_included', []).filter(Boolean);
  const excludedSegmentIds = campaign.get('segments_excluded', []).filter(Boolean);

  const goals = campaign.isMailrailPartnerMessage
    ? Goals.newListResponse()
    : Goals.list({
        filters: {
          _parent: campaign.id,
          is_archived__ne: true,
        },
      });
  const segments = campaign.isMailrailPartnerMessage
    ? Segments.newListResponse()
    : Segments.list({
        filters: {
          id__in: [...includedSegmentIds, ...excludedSegmentIds],
          is_archived__ne: true,
        },
      });
  // Used for validation only.
  const emailVerifications = getEmailVerifications(campaign, EmailVerifications);

  // Converting observables back to vanilla JavaScript.
  const childModels: Model[] = [...templates, ...segments.models.slice(), ...goals.models.slice()];
  const grandchildModels: Model[] = [...getGoalSegments(goals.models, Segments)];

  // Pre-compute step errors like DripCampaignEdit
  const goalSegments = getGoalSegments(goals.models, Segments);
  const emailVerification = campaign.getEmailVerification(emailVerifications);

  // A drip validates the campaign document per-card in the Builder, so it never runs Configure
  // Details (detailsErrors stays empty and is unused in its stepErrorsList slot).
  const detailsErrors = campaign.isMailrailDrip
    ? []
    : flattenErrors(validateStepConfigureDetails(campaign, emailVerification, template));
  const dripStepsErrors = campaign.isMailrailDrip
    ? validateStepDripBuilder(campaign as MailrailDripEmailCampaign, emailVerifications, templates)
        .dripStepsErrors
    : [];
  const automationErrors = campaign.isMailrailAutoSend
    ? flattenErrors(validateStepAutomation(campaign as MailrailAutoSendEmailCampaign))
    : [];
  const goalsOrValueErrors = campaign.isMonetization
    ? flattenErrors(validateStepValue(campaign))
    : flattenErrors(validateStepGoals({ goals: goals.models, goalSegments }));
  const sendErrors = flattenErrors(validateStepScheduleSend(campaign));

  const stepErrorsList = [
    // Drip replaces Configure Details with the Builder step (and drops the top-level Test step).
    ...(campaign.isMailrailDrip ? [dripStepsErrors] : [detailsErrors]),
    ...(campaign.isMailrailAutoSend ? [automationErrors] : []),
    ...(campaign.isMailrailPartnerMessage ? [] : [goalsOrValueErrors]),
    ...(campaign.isMailrailDrip ? [] : [[]]),
    sendErrors,
  ];

  const getStepErrors = useCallback(
    (step: number): string[] => {
      if (step === -1) {
        return [];
      }
      return stepErrorsList[step] ?? [];
    },
    [campaign, emailVerification, goalSegments, goals.models, stepErrorsList, template],
  );

  const getCompletedStepMap = useCallback((): IWizardStepsCompleted => {
    return stepErrorsList.reduce<IWizardStepsCompleted>((map, errors, index) => {
      if (index === 0) {
        // For a drip, don't treat the Builder step as complete until at least one step has been
        // seeded (mirrors the legacy DripCampaignEdit guard) so a fresh draft always opens on Build.
        const dripStepsSeeded =
          !campaign.isMailrailDrip || (campaign.get('steps')?.length ?? 0) > 0;
        map[index] = !errors.length && !emailVerifications?.isPending && dripStepsSeeded;
      } else {
        map[index] = !errors.length;
      }
      return map;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, stepErrorsList, emailVerifications?.isPending]);

  const isReady: () => boolean = () => {
    return !(
      campaign.isPending ||
      goals.isPending ||
      segments.isPending ||
      emailVerifications?.isPending ||
      templates.some((t) => t.isEphemeral || t.isPending)
    );
  };

  const { currentStep, completeStep, lastStep, onNext, onPrev, onChangeStep } = useWizardState({
    getCompletedStepMap,
    waitFor: isReady,
    skipToMaxStep: [
      CampaignState.Published,
      CampaignState.Publishing,
      CampaignState.Stopped,
    ].includes(campaign.get('state', CampaignState.Draft)),
    deps: [template],
  });

  // TODO: Define an organized way of validating models in draft state, like a `validateDraft` method
  function buttonValidateSave(): ICampaignValidationErrors {
    if (campaign.isMailrailPartnerMessage) {
      return {};
    }
    const errors: ICampaignValidationErrors = {
      segments_included: [],
      segments_excluded: [],
      predicates: [],
    };
    if (campaign.get('segments_included').some((v) => !v)) {
      errors['segments_included'] = ['Finish selecting a group or remove the placeholder group.'];
    }
    if (campaign.get('segments_excluded').some((v) => !v)) {
      errors['segments_excluded'] = ['Finish selecting a group or remove the placeholder group.'];
    }
    // The comparisons "exists" and "not exists" do not require a value.
    const filteredPredicates = campaign
      .get('predicates', [])
      .filter(({ comparison }) => !(['exists', 'nexists'] as unknown[]).includes(comparison));
    // True and false are valid values, so we only want to check for undefined, empty string, and null.
    if (
      filteredPredicates.some(({ value }) => (['', undefined, null] as unknown[]).includes(value))
    ) {
      errors['predicates'] = [{ value: ['Trigger value cannot be blank.'] }];
    }
    return errors;
  }

  function buttonValidatePublish(): ICampaignValidationErrors {
    if (
      !account ||
      !emailVerifications ||
      campaign.isPending ||
      emailVerifications.isPending ||
      goals.isPending ||
      segments.isPending
    ) {
      return {};
    }
    const emailVerification = campaign.getEmailVerification(emailVerifications);
    const goalSegments = getGoalSegments(goals.models, Segments);
    return validate({
      account,
      campaign,
      emailVerification,
      emailVerifications,
      goals: goals.models,
      goalSegments,
      domains: domains.models,
      segments: segments.models,
      t,
      template,
      templates,
      flags,
    });
  }

  async function preSave(): Promise<void> {
    // Ensures ephemeral segments are added to the store before campaign save.
    await Promise.all(
      segments.models
        .filter((s) => s.id)
        .map(async (s) => {
          await when(() => !s.isPending);
          if (s.isEphemeral) {
            await Segments.add(s);
          }
        }),
    );
  }

  async function postStateChange(campaign: Campaign, stateChanged?: boolean): Promise<void> {
    if (campaign instanceof MailrailSingleSendEmailCampaign && stateChanged) {
      // The template's read_only field was toggled on the backend, so we must reload it
      await Promise.all(
        templates.map(async (t) => {
          t.reload();
        }),
      );
    }
  }

  const guards = useMailrailSaveGuards({
    campaign,
    templates,
    childModels,
    grandchildModels,
    preSave,
    postSave: postStateChange,
  });

  const isRepublish = campaign.isStopped || campaign.isComplete;
  const navSaveButton = (
    <SaveCampaignButton
      campaign={campaign}
      childModels={childModels}
      grandchildModels={grandchildModels}
      postSave={guards.continueAfterSave}
      preSave={preSave}
      shouldChangeState={isRepublish}
      showIcon={true}
      validate={isRepublish ? buttonValidatePublish : buttonValidateSave}
    />
  );

  const wizardProps = {
    campaign,
    disabled: !campaign.isEditable,
    domains: domains.models,
    goals: goals.models,
    onNext,
    onPrev,
    validate,
    isVerifiedDomainSender: getIsVerifiedDomainSender(
      domains.models,
      campaign.getEmailVerification(emailVerifications),
      flags.bypassMX,
    ),
    validationErrors: buttonValidatePublish(),
  };

  const steps: IWizardStepConfig[] = [
    // Drip campaigns edit the campaign-as-step-0 plus embedded steps in the Builder, which replaces
    // both Configure Details and Automation.
    ...(campaign.isMailrailDrip
      ? [
          {
            key: 'builder',
            title: t('Build'),
            errors: dripStepsErrors,
            containerClass: styles.containerWide,
            render: (): JSX.Element => (
              <MailrailCampaignStepDripBuilder
                campaign={campaign as MailrailDripEmailCampaign}
                disabled={!campaign.isEditable}
                emailVerifications={emailVerifications}
                templates={templates}
              />
            ),
          },
        ]
      : [
          {
            key: 'details',
            title: t('Configure Details'),
            errors: detailsErrors,
            containerClass: styles.container,
            render: (): JSX.Element => <MailrailCampaignStepConfigureDetails {...wizardProps} />,
          },
        ]),
    ...(campaign.isMailrailAutoSend
      ? [
          {
            key: 'automation',
            title: t('Automation'),
            errors: automationErrors,
            containerClass: styles.containerWide,
            render: (): JSX.Element => (
              <MailrailCampaignStepAutomation
                {...wizardProps}
                campaign={campaign as MailrailAutoSendEmailCampaign}
              />
            ),
          },
        ]
      : []),
    ...(campaign.isMailrailPartnerMessage
      ? []
      : [
          campaign.isMonetization
            ? {
                key: 'value',
                title: t('Set Value'),
                errors: goalsOrValueErrors,
                containerClass: styles.container,
                render: (): JSX.Element => <StepValue {...wizardProps} />,
              }
            : {
                key: 'goals',
                title: t('Set Goals'),
                errors: goalsOrValueErrors,
                containerClass: styles.containerWide,
                render: (): JSX.Element => <StepGoals {...wizardProps} />,
              },
        ]),
    ...(campaign.isMailrailDrip
      ? []
      : [
          {
            key: 'test',
            title: t('Test'),
            errors: [],
            containerClass: styles.container,
            render: (): JSX.Element => (
              <MailrailCampaignStepTest {...wizardProps} disabled={false} />
            ),
          },
        ]),
    {
      key: 'send',
      title:
        campaign.isMailrailAutoSend || campaign.isMailrailDrip
          ? t('Schedule & Publish')
          : t('Schedule & Send'),
      errors: sendErrors,
      containerClass: styles.containerWide,
      render: (): JSX.Element => (
        <MailrailCampaignStepScheduleSend {...wizardProps} stepNumber={steps.length} />
      ),
    },
  ];

  const stepsHeader = (
    <Steps completed={completeStep} current={currentStep} onChange={onChangeStep} usePortal={true}>
      {steps.map((step, index) => (
        <Step key={step.key} stepIndex={index} title={step.title} />
      ))}
    </Steps>
  );

  const activeStep = steps[currentStep];

  return (
    <div data-appcues-campaign={campaign.get('_cls')}>
      <Wizard
        isFullWidth={true}
        isLoading={currentStep < 0}
        layout={'horizontal'}
        steps={stepsHeader}
      >
        <CampaignWizardActionBar
          campaign={campaign}
          childModels={childModels}
          currentStep={currentStep}
          getStepErrors={getStepErrors}
          grandchildModels={grandchildModels}
          isLoading={!isReady() || currentStep < 0}
          lastStep={lastStep}
          onNext={onNext}
          onPrev={onPrev}
          onPublishClick={guards.onPublishClick}
          onSaveClick={guards.onSaveClick}
          postSave={postStateChange}
          preSave={preSave}
          validate={buttonValidatePublish}
          validateSave={buttonValidateSave}
        />

        {activeStep && <div className={activeStep.containerClass}>{activeStep.render()}</div>}
      </Wizard>
      {guards.confirmVariant && (
        <MailrailChangeConfirmModal
          campaign={campaign}
          onClose={guards.closeConfirmModal}
          onConfirm={
            guards.confirmVariant === 'republish' ? guards.confirmRepublish : guards.confirmLiveSave
          }
          onDiscard={guards.discardAndContinue}
          opened={true}
          saveButton={navSaveButton}
          variant={guards.confirmVariant}
        />
      )}
      <EmailAudienceConfirmModal
        campaign={campaign}
        onClose={guards.closeAudienceModal}
        onConfirm={guards.confirmAudience}
        opened={guards.isAudienceModalOpen}
      />
      <PendingImportsConfirmModal
        onClose={guards.closePendingImportsModal}
        onConfirm={guards.publishAnyway}
        opened={guards.isPendingImportsModalOpen}
      />
    </div>
  );
}

export default observer(MailrailCampaignEdit);
